import Konva from "konva";
import {
  CUBE_HEIGHT_LARGE,
  CUBE_HEIGHT_SMALL,
  CUBE_HEIGHT_TALL,
  CUBE_HEIGHT_WIDE,
  CUBE_LARGE_2D_HEIGHT,
  CUBE_LARGE_2D_WIDTH,
  CUBE_SIZE_LARGE,
  CUBE_SIZE_SMALL,
  CUBE_SIZE_SMALL_CORNER,
  CUBE_SIZE_TALL,
  CUBE_SIZE_TALL_CORNER,
  CUBE_SIZE_WIDE,
  CUBE_SMALL_2D_HEIGHT,
  CUBE_SMALL_2D_WIDTH,
  CUBE_TALL_2D_HEIGHT,
  CUBE_TALL_2D_WIDTH,
  CUBE_WIDE_2D_HEIGHT,
  CUBE_WIDE_2D_WIDTH,
  CUBE_WIDTH_LARGE,
  CUBE_WIDTH_SMALL,
  CUBE_WIDTH_TALL,
  CUBE_WIDTH_WIDE,
  CubeSize,
} from "./global-constants";
import { BoxService, ColorType, CreateBoxCmd, ViewType } from "./box-service";
import { Images } from "./images";
import { ServiceLocator } from "./service-locator";
import { ExportedBoxUtils } from "./box-utils";
import { ExportedBox2D } from "./box-2d";
import { ExportedBoxIso } from "./box-iso";
import { ExportedBoxMath } from "./box-math";
import { ExportedBoxIsoDebug } from "./box-iso-debug";

interface KonvaBox {
  readonly attrs: { [id: string]: any };
  setAttr(attrName: string, attrVal: any): void;
  setWidth(size: number): void;
  setHeight(size: number): void;
  setFillPatternScaleX(size: number): void;
  setFillPatternScaleY(size: number): void;
  destroy(): void;
}

interface ShelfDimensions {
  readonly imgWidth: number;
  readonly imgHeight: number;
  readonly renderWidth: number;
  readonly renderHeight: number;
  readonly scaleX: number;
  readonly scaleY: number;
}

export interface BoxConfig {
  readonly size: CubeSize;
  readonly x?: number;
  readonly y?: number;
  readonly scaleX?: number;
  readonly scaleY?: number;
  readonly width?: number;
  readonly height?: number;
  readonly back?: "0" | "n" | "w" | undefined;
  readonly door?: "0" | "n" | "w" | undefined;
  readonly drawers?: "000" | "n" | "w" | undefined;
  readonly shelf?: "0000" | "n" | "w" | undefined;
  readonly direction?: "r" | "l" | undefined;
  readonly crossDivider?: "0" | "n" | "w" | undefined;
}

export class ExportedStage {
  public readonly viewIso: ViewType = "iso";
  public readonly view2D: ViewType = "2d";

  public readonly render2dSmallWidth: number = 212;
  public readonly render2dWideWidth: number = 309;
  public readonly render2dTallWidth: number = 212;
  public readonly render2dLargeWidth: number = 309;
  public readonly renderIsoSmallWidth: number = 336;
  public readonly renderIsoTallWidth: number = 336;
  public readonly renderIsoWideWidth: number = 431.5;
  public readonly renderIsoLargeWidth: number = 432;

  public readonly render2dSmallHeight: number = 229;
  public readonly render2dWideHeight: number = 229;
  public readonly render2dTallHeight: number = 334;
  public readonly render2dLargeHeight: number = 334;
  public readonly renderIsoSmallHeight: number = 336;
  public readonly renderIsoTallHeight: number = 439.7;
  public readonly renderIsoWideHeight: number = 352;
  public readonly renderIsoLargeHeight: number = 456.9;

  public readonly boxService: BoxService;
  public readonly imagesService: Images;
  public readonly exportedBoxUtils: ExportedBoxUtils;
  public readonly exportedBoxIso: ExportedBoxIso;
  public readonly exportedBoxIsoDebug: ExportedBoxIsoDebug;
  public readonly exportedBox2D: ExportedBox2D;
  public readonly exportedBoxMath: ExportedBoxMath;

  public refocusSetTimeoutId: number | undefined = undefined;
  public currentView: ViewType = this.viewIso;
  public isDebug: boolean = false;

  public stage: Konva.Stage | undefined = undefined;
  public layer: Konva.Layer | undefined = undefined;

  public constructor() {
    this.boxService = ServiceLocator.boxService;
    this.imagesService = ServiceLocator.images;
    this.exportedBoxUtils = new ExportedBoxUtils();
    this.exportedBoxIso = new ExportedBoxIso();
    this.exportedBoxIsoDebug = new ExportedBoxIsoDebug();
    this.exportedBox2D = new ExportedBox2D();
    this.exportedBoxMath = new ExportedBoxMath();
  }

  public drawBoxDebug(box) {
    if (this.currentView === this.viewIso) {
      this.exportedBoxIsoDebug.drawCube(this.layer, box);
    } else {
      this.exportedBox2D.drawBoxDebugElements(this.layer, box);
    }
  }

  public getShelfDimensions(cubeSize: CubeSize): ShelfDimensions {
    let renderHeight, renderWidth, imgWidth, imgHeight;

    if (this.currentView === this.viewIso) {
      switch (cubeSize) {
        case CUBE_SIZE_SMALL:
        case CUBE_SIZE_SMALL_CORNER:
          renderWidth = this.renderIsoSmallWidth;
          renderHeight = this.renderIsoSmallHeight;
          imgWidth = CUBE_WIDTH_SMALL;
          imgHeight = CUBE_HEIGHT_SMALL;
          break;
        case CUBE_SIZE_TALL:
        case CUBE_SIZE_TALL_CORNER:
          renderWidth = this.renderIsoTallWidth;
          renderHeight = this.renderIsoTallHeight;
          imgWidth = CUBE_WIDTH_TALL;
          imgHeight = CUBE_HEIGHT_TALL;
          break;
        case CUBE_SIZE_WIDE:
          renderWidth = this.renderIsoWideWidth;
          renderHeight = this.renderIsoWideHeight;
          imgWidth = CUBE_WIDTH_WIDE;
          imgHeight = CUBE_HEIGHT_WIDE;
          break;
        case CUBE_SIZE_LARGE:
          renderWidth = this.renderIsoLargeWidth;
          renderHeight = this.renderIsoLargeHeight;
          imgWidth = CUBE_WIDTH_LARGE;
          imgHeight = CUBE_HEIGHT_LARGE;
          break;
      }
    } else {
      switch (cubeSize) {
        case CUBE_SIZE_SMALL:
        case CUBE_SIZE_SMALL_CORNER:
          renderWidth = this.render2dSmallWidth;
          renderHeight = this.render2dSmallHeight;
          imgWidth = CUBE_SMALL_2D_WIDTH;
          imgHeight = CUBE_SMALL_2D_HEIGHT;
          break;
        case CUBE_SIZE_WIDE:
          renderWidth = this.render2dWideWidth;
          renderHeight = this.render2dWideHeight;
          imgWidth = CUBE_WIDE_2D_WIDTH;
          imgHeight = CUBE_WIDE_2D_HEIGHT;
          break;
        case CUBE_SIZE_TALL:
        case CUBE_SIZE_TALL_CORNER:
          renderWidth = this.render2dTallWidth;
          renderHeight = this.render2dTallHeight;
          imgWidth = CUBE_TALL_2D_WIDTH;
          imgHeight = CUBE_TALL_2D_HEIGHT;
          break;
        case CUBE_SIZE_LARGE:
          renderWidth = this.render2dLargeWidth;
          renderHeight = this.render2dLargeHeight;
          imgWidth = CUBE_LARGE_2D_WIDTH;
          imgHeight = CUBE_LARGE_2D_HEIGHT;
          break;
      }
    }

    return {
      imgWidth,
      imgHeight,
      renderWidth,
      renderHeight,
      scaleX: renderWidth / (imgWidth + 1),
      scaleY: renderHeight / (imgHeight + 1),
    };
  }

  public addShelf(config: BoxConfig): any {
    const cube_size = config.size;

    const dimensions = this.getShelfDimensions(cube_size);

    const cmd: CreateBoxCmd = {
      size: config.size,
      x: config.x || -this.stage.x() / this.stage.scaleX() + 80,
      y: config.y || -this.stage.y() / this.stage.scaleX() + 80,
      scaleX: dimensions.scaleX,
      scaleY: dimensions.scaleY,
      width: dimensions.renderWidth,
      height: dimensions.renderHeight,
      back: config.back,
      door: config.door,
      drawers: config.drawers,
      shelf: config.shelf,
      direction: config.direction,
      crossDivider: config.crossDivider,
    };

    const box = this.boxService.createBox(
      cmd,
      (boxProps) => new Konva.Rect(boxProps) as KonvaBox,
    ) as any;

    if (box) {
      box.on("dragstart", () => {
        box.moveToTop();
      });
      box.on("dragmove", (event) => {
        const currentBox = box;

        currentBox.strokeWidth(0);

        this.exportedBoxUtils.setPosition(
          currentBox,
          currentBox.attrs.x,
          currentBox.attrs.y,
        );

        for (const b of this.boxService.getKonvoBoxes()) {
          if (currentBox !== b) {
            if (!event.evt.shiftKey) {
              let newPosition;

              if (this.currentView === this.viewIso) {
                newPosition = this.exportedBoxIso.getPositionAfterSnaps(
                  currentBox,
                  b,
                );
              } else {
                newPosition = this.exportedBox2D.getPositionAfterSnaps(
                  currentBox,
                  b,
                );
              }

              this.exportedBoxUtils.setPosition(
                currentBox,
                newPosition.x,
                newPosition.y,
              );
            }
          }
        }

        this.layer.draw();

        this.refocusBoxes();
      });
      box.on("dragend", () => {
        this.drawDimensions();

        this.drawBoxDebug(box);

        this.layer.draw();
      });

      this.layer.add(box);

      this.drawBox(box);

      this.drawDimensions();

      this.drawBoxDebug(box);
    }

    return box;
  }

  public toggleColor() {
    this.boxService.toggleColor();

    for (const box of this.boxService.getKonvoBoxes()) {
      this.drawBox(box);
    }
  }

  public drawDimensions() {
    if (this.currentView === this.viewIso) {
      this.exportedBoxIso.drawDimensions(
        this.layer,
        this.boxService.getKonvoBoxes(),
      );
    } else {
      this.exportedBox2D.drawDimensions(
        this.layer,
        this.boxService.getKonvoBoxes(),
      );
    }

    this.layer.draw();
  }

  public refocusBoxes() {
    if (this.currentView === this.viewIso) {
      if (this.refocusSetTimeoutId) {
        clearTimeout(this.refocusSetTimeoutId);
      }

      this.refocusSetTimeoutId = setTimeout(() => {
        const focusBoxes = [...this.boxService.getKonvoBoxes()];
        const orderedBoxes = [];

        while (focusBoxes.length > 0) {
          let frontMostBoxIndex = 0;
          let attempts = 1,
            movement = true;

          while (movement && attempts <= 10) {
            movement = false;

            for (let i = 0; i < focusBoxes.length; i++) {
              if (
                frontMostBoxIndex !== i &&
                this.exportedBoxMath.isUnderBox(
                  focusBoxes[frontMostBoxIndex],
                  focusBoxes[i],
                )
              ) {
                frontMostBoxIndex = i;

                movement = true;
              }
            }

            attempts++;
          }

          orderedBoxes.push(focusBoxes[frontMostBoxIndex]);
          focusBoxes.splice(frontMostBoxIndex, 1);
        }

        this.exportedBoxIsoDebug.drawFocusOrder(this.layer, orderedBoxes);

        for (let i = orderedBoxes.length - 1; i >= 0; i--) {
          orderedBoxes[i].moveToTop();
        }

        this.exportedBoxIsoDebug.focus();

        this.layer.draw();
      }, 50);
    }
  }

  public changeIsDebug(debug: boolean): void {
    this.isDebug = debug;

    this.exportedBoxUtils.changeIsDebug(debug);
    this.exportedBoxIso.changeIsDebug(debug);
    this.exportedBoxIsoDebug.changeIsDebug(debug);
    this.exportedBox2D.changeIsDebug(debug);
  }

  public printBoxDebug(): void {
    for (const box of this.boxService.getKonvoBoxes()) {
      const index = box.attrs.box_index;
      const x = box.attrs.x;
      const y = box.attrs.y;
      const width = box.attrs.width;
      const height = box.attrs.height;

      console.log(`box ${index}: (${x},${y}) width=${width} height=${height}`);
    }
  }

  public zoom(scaleIn: boolean): void {
    const scaleBy = 1.1;
    const oldScale = this.stage.scaleX();

    const pointer = {
      x: this.stage.width() / 2,
      y: this.stage.height() / 2,
    };

    const mousePointTo = {
      x: (pointer.x - this.stage.x()) / oldScale,
      y: (pointer.y - this.stage.y()) / oldScale,
    };

    const newScale = scaleIn ? oldScale * scaleBy : oldScale / scaleBy;

    this.stage.scale({ x: newScale, y: newScale });

    const newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };
    this.stage.position(newPos);

    this.stage.draw();
  }

  public setZoom(zoom: number) {
    this.stage.scale({ x: zoom, y: zoom });

    this.stage.draw();
  }

  public getZoom(): number {
    const zoom = this.stage.scaleX();

    return zoom;
  }

  public getStagePosition(): { x: number; y: number } {
    const position = {
      x: this.stage.x() / this.stage.scaleX(),
      y: this.stage.y() / this.stage.scaleX(),
    };

    return position;
  }

  public setStagePosition(x: number, y: number): void {
    const position = {
      x: x * this.stage.scaleX(),
      y: y * this.stage.scaleX(),
    };

    this.stage.position(position);

    this.stage.draw();
  }

  public getStage() {
    return this.stage;
  }

  public setStage(stage: Konva.Stage) {
    this.stage = stage;
  }

  public getLayer() {
    return this.layer;
  }

  public setLayer(layer: Konva.Layer) {
    this.layer = layer;
  }

  public getBoxes(): KonvaBox[] {
    return this.boxService.getKonvoBoxes();
  }

  public getColor(): ColorType {
    return this.boxService.getColor();
  }

  public setColor(color: ColorType): void {
    this.boxService.setColor(color);
  }

  public getView(): string {
    return this.currentView;
  }

  public setView(view: string): void {
    if (view === this.view2D) {
      this.currentView = this.view2D;
    } else {
      this.currentView = this.viewIso;
    }
  }

  public isIsoView(): boolean {
    return this.currentView === this.viewIso;
  }

  public drawBoxes(): void {
    for (const box of this.boxService.getKonvoBoxes()) {
      this.drawBox(box);

      this.drawBoxDebug(box);
    }

    this.drawDimensions();
  }

  public drawBox(box): void {
    const image = this.boxService.getImage(this.currentView, box);

    box.fillPatternImage(image);

    this.stage.draw();
  }

  public toggleView(): void {
    const boxes = this.boxService.getKonvoBoxes();

    if (this.currentView === this.viewIso) {
      this.currentView = this.view2D;

      this.exportedBoxIso.clearDimensions();
      this.exportedBoxIsoDebug.clearAll();

      for (let i = 0; i < boxes.length; i++) {
        const box = boxes[i];
        const cubeSize = this.exportedBoxUtils.getCubeSize(box);

        const renderOrigin =
          this.exportedBoxMath.get2DCubeOriginFromIsoBox(box);

        const newX = renderOrigin.x;
        const newY = renderOrigin.y;

        const dimensions = this.getShelfDimensions(cubeSize);
        const newWidth = dimensions.renderWidth;
        const newHeight = dimensions.renderHeight;

        this.exportedBoxUtils.setPosition(box, newX, newY);

        box.setWidth(newWidth);
        box.setHeight(newHeight);
        box.setFillPatternScaleX(dimensions.scaleX);
        box.setFillPatternScaleY(dimensions.scaleY);

        this.drawBox(box);
      }
    } else {
      this.currentView = this.viewIso;

      this.exportedBox2D.clearDimensions();
      this.exportedBox2D.clearBoxDebugElements();

      for (let i = 0; i < boxes.length; i++) {
        const box = boxes[i];
        const cubeSize = this.exportedBoxUtils.getCubeSize(box);

        const dimensions = this.getShelfDimensions(cubeSize);
        const newWidth = dimensions.renderWidth;
        const newHeight = dimensions.renderHeight;

        const cubeOrigin =
          this.exportedBoxMath.getIsoCubeOriginFrom2DCubeOrigin(box);
        const imgOrigin = this.exportedBoxMath.getIsoImgOriginFromIsoCubeOrigin(
          cubeSize,
          cubeOrigin.x,
          newWidth,
          cubeOrigin.y,
          newHeight,
        );

        this.exportedBoxUtils.setPosition(box, imgOrigin.x, imgOrigin.y);
        box.setWidth(newWidth);
        box.setHeight(newHeight);
        box.setFillPatternScaleX(dimensions.scaleX);
        box.setFillPatternScaleY(dimensions.scaleY);

        this.drawBox(box);
      }
    }

    this.drawDimensions();

    this.refocusBoxes();
  }

  public getBoxService(): BoxService {
    return this.boxService;
  }

  public getImagesService(): Images {
    return this.imagesService;
  }
}
